import { datadogRum } from '@datadog/browser-rum';
import queryString from 'query-string';
import {
  all,
  fork,
  takeLatest,
  take,
  call,
  select,
  put,
} from 'redux-saga/effects';

import { FetchError } from '@frontend/api';
import { fullstoryIdentify } from '@frontend/services';

import {
  IDP_URL,
  SERVER,
  MARKETING_SITE_URL,
  AUTHENTIK_ENABLED,
} from 'constants/all';

import { initTwoFactorAuthentication } from 'modules/auth/twoFactorAuthentication/data/actions';
import { sesVerificationFailureModal } from 'modules/common/modals/sesVerificationFailureModal/data/actions';

import API from 'services/api';
import authService from 'services/authService';
import teamService from 'services/teamService';
import { chameleon } from 'services/thirdParty';
import socketSingleton from 'services/webSocketService';

import { getHomePage } from 'store/app/selectors';
import { PAGE_REQUEST } from 'store/app/types';
import {
  receiveUser,
  receiveAccessToken,
  receivePaperlessTerms,
} from 'store/session/actions';
import { getIsLoggedIn, getUser } from 'store/session/selectors';

import history from 'utils/browserHistory';
import { getErrorMessage } from 'utils/stringUtils';
import {
  consumeReauthRequired,
  getToken,
  setToken,
  removeToken,
  setReauthRequired,
} from 'utils/token';

import {
  types,
  discover,
  login,
  logoutSuccess,
  acceptTerms as acceptTermsAction,
} from './actions';
import { AUTH_STEPS, DISCOVER_NEXT } from './constants';

export function* initLogin(loc) {
  const isLoggedIn = yield select(getIsLoggedIn);
  const isFromLoginPage = history.location.pathname !== '/login';
  if (isLoggedIn && !isFromLoginPage) {
    const next = queryString.parse(loc.location.search).next;
    next ? history.push(next) : history.push(yield select(getHomePage));
  }
}

/**
 * Step 1 of login: ask the backend how this email should authenticate.
 *
 * `oidc` and `saml` navigate the browser away and never resolve. `password`
 * advances to step 2. Anything else — an unrecognized `next` value, or any
 * failure at all — also advances to step 2, because password auth still works
 * for every user, including those already provisioned in Authentik. Discover
 * is a routing optimization, so it must never be able to block a sign-in.
 */
export function* watchDiscover(action) {
  const { email, next } = action.payload;
  try {
    // Set by an explicit sign-out. Asks Authentik to re-authenticate rather
    // than issuing a code from a session that outlived our tokens. Consumed
    // here so the challenge is forced exactly once.
    const reauthRequired = yield call(consumeReauthRequired);
    const response = yield call(authService.discover, {
      email,
      next: next || '/',
      ...(reauthRequired && { prompt: 'login' }),
    });
    switch (response.next) {
      case DISCOVER_NEXT.OIDC:
        window.location.replace(response.oidcUrl);
        return;
      case DISCOVER_NEXT.SAML:
        window.location.replace(response.ssoUrl);
        return;
      default:
        yield put(discover.success(AUTH_STEPS.PASSWORD));
    }
  } catch (error) {
    console.warn(
      'Login discovery failed; falling back to password auth',
      error,
    );
    yield put(discover.success(AUTH_STEPS.PASSWORD));
  }
}

export function* watchLogin(action) {
  try {
    const { credentials, next } = action.payload;
    const nextUrl = decodeURIComponent(next || '');
    const response = yield call(authService.login, credentials);
    if (response.token) yield call(loginWithToken, response.token, nextUrl);
    else if (response.isTwoFactorAuthRequired) {
      history.push('/authenticate');
      const action = yield take(PAGE_REQUEST);
      if (action.payload.location?.pathname === '/authenticate') {
        yield put(
          initTwoFactorAuthentication({
            username: credentials.username,
            password: credentials.password,
            next: nextUrl,
          }),
        );
      }
    }
  } catch (error) {
    yield put(login.error(getErrorMessage(error, 'Unknown error')));
  }
}

export function* loginWithToken(accessToken, nextUrl = null) {
  yield call(setToken, accessToken);
  const user = yield call(authService.profile);
  yield call(prefetchData);
  yield put(receiveUser(user));
  yield put(receiveAccessToken(accessToken));
  yield put(login.success(accessToken, user));
  const redirected = yield call(submitIDPForm, nextUrl);
  if (!redirected) {
    history.push(nextUrl || (yield select(getHomePage)));
  }
}

export function* initializeLogin() {
  let accessToken = yield call(getToken);
  if (!accessToken) {
    try {
      const response = yield call(authService.getTokenAfterSSO);
      if (response) {
        accessToken = response.token;
        yield call(setToken, accessToken);
      }
    } catch (error) {
      yield call(removeToken);
    }
  }
  if (accessToken) {
    try {
      const user = yield call(authService.profile);
      yield call(prefetchData);
      yield put(receiveUser(user));
      yield put(receiveAccessToken(accessToken));
      yield put(login.success(accessToken, user));
      yield call(submitIDPForm);
    } catch (error) {
      if (!(error instanceof FetchError) || error.statusCode !== 429) {
        yield call(removeToken);
      }
    }
  }
}

export function* prefetchData() {
  const paperlessTerms = yield call(API.general.paperlessTerms);
  yield put(receivePaperlessTerms(paperlessTerms));
}

/**
 * Generate a boolean stating whether or not the window
 * was navigated away from. When the next query param is
 * /idp/login/process/, this generator will navigate the window
 * to a self-submitting HTML form returned from the SAML IDP.
 *
 * @param {string} next - next query param
 * @yields {boolean}
 */
export function* submitIDPForm(next) {
  const nextStep = next || queryString.parse(window.location.search).next;
  if (nextStep?.startsWith('/idp')) {
    window.location = IDP_URL + nextStep;
    yield true;
  }
  yield false;
}

export function* watchLoginSuccess(action) {
  const { user } = action.payload;
  const featureFlags = user?.group?.featureFlags || {};
  yield call(fullstoryIdentify, user.username, {
    email: user.email,
    displayName: `${user.firstName} ${user.lastName}`,
    ...featureFlags,
  });
  yield call(chameleon.identify, user.id, {
    firstName: user.firstName,
    leadSource: user.leadSource,
    dateJoined: user.dateJoined,
    isAdmin: user.groupMember?.isAdmin,
    email: user.email,
    company: {
      name: user.group?.name,
      id: user.group?.id,
      leadSource: user.group?.leadSource,
      featureFlags,
      role: user.group?.role,
      userStatus: {
        isSuperuser: user.isSuperuser,
        isTwoFactorAuthEnabled: user.isTwoFactorAuthEnabled,
      },
      groupStatus: {
        hasCustomerSupportRecord: user.group?.hasCustomerSupportRecord,
        hasStripeAccount: user.group?.hasStripeAccount,
      },
    },
  });

  yield call(datadogRum.setUser, {
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    isSuperuser: user.isSuperuser,
    isTwoFactorAuthEnabled: user.isTwoFactorAuthEnabled,
    isAdmin: user.groupMember?.isAdmin,
    leadSource: user.leadSource,
    dateJoined: user.dateJoined,
  });

  yield call(datadogRum.setAccount, {
    id: user.group?.id,
    name: user.group?.name,
    leadSource: user.group?.leadSource,
    featureFlags: user.group?.featureFlags,
    role: user.group?.role,
    hasCustomerSupportRecord: user.group?.hasCustomerSupportRecord,
    hasStripeAccount: user.group?.hasStripeAccount,
  });

  const isSesVerified = queryString.parse(
    window.location.search,
  ).is_ses_verified;
  if (isSesVerified === 'true')
    yield call(teamService.getIsEmailVerified, user.id);
  else if (isSesVerified === 'false')
    yield put(sesVerificationFailureModal.open());
}

export function* watchLogout() {
  for (;;) {
    yield take(types.LOGOUT_REQUEST);
    // Read this before finishLogout clears the user out of the store.
    const user = yield select(getUser);
    if (AUTHENTIK_ENABLED && user?.authentikId) {
      // Revoking our tokens does not touch the Authentik SSO session, so the
      // next sign-in would be served silently from it. Flag the sign-out so
      // the next discover asks Authentik to re-authenticate.
      //
      // Deliberately does NOT end the SSO session: other applications behind
      // Authentik share it, and signing out of this app must not sign the
      // user out of those.
      yield call(setReauthRequired);
    }
    yield call(finishLogout);
    if (SERVER === 'production') {
      window.location.href = MARKETING_SITE_URL;
    } else {
      history.push('/login');
    }
  }
}

export function* finishLogout() {
  try {
    yield call(authService.logoutAll);
    yield call(removeToken);
    yield put(receiveAccessToken(null));
    yield put(receiveUser(null));
    socketSingleton.shutDown();
    yield put(logoutSuccess());
  } catch (e) {
    console.error('There was an error logging out: ', e);
  }
}

export function* watchAcceptTerms(action) {
  const { acceptedTerms } = action.payload;
  yield acceptTerms(acceptedTerms);
}

function* acceptTerms(acceptedTerms) {
  try {
    yield all(
      acceptedTerms.map((terms) => call(authService.acceptTerms, terms)),
    );
    yield put(acceptTermsAction.success());
  } catch (error) {
    yield put(acceptTermsAction.error({ error }));
  }
}

export default function* root() {
  yield takeLatest(types.DISCOVER, watchDiscover);
  yield takeLatest(types.LOGIN, watchLogin);
  yield takeLatest(types.LOGIN_SUCCESS, watchLoginSuccess);
  yield takeLatest(types.ACCEPT_TERMS_REQUEST, watchAcceptTerms);
  yield fork(watchLogout);
}
